import type { PlausibilityMode } from "@sitescene/types";

import { useState } from "react";

import { Button } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	usePlausibilitySettings,
	useUpdatePlausibilityMode,
} from "@/features/plausibility/use-plausibility-settings";

import { BackfillPanel } from "./BackfillPanel";
import { ReverifyPanel } from "./ReverifyPanel";

const MODES: { description: string; value: PlausibilityMode }[] = [
	{
		description:
			"The verifier ignores this organization. No rows, no model calls.",
		value: "off" as PlausibilityMode,
	},
	{
		description:
			"Every alert-worthy detection is verified and the outcome recorded. Measurements, events, and notifications are untouched.",
		value: "shadow" as PlausibilityMode,
	},
	{
		description:
			"Verification rewrites measurement severity. Notifications for 5+ events are held up to two minutes for the verdict; a down-rank under the alert line sends nothing.",
		value: "live" as PlausibilityMode,
	},
];

/** Per-organization rollout switch for plausibility verification. */
export const PlausibilitySettings = () => {
	const { selected } = useOrgSelection();
	const settings = usePlausibilitySettings();
	const update = useUpdatePlausibilityMode();
	const [draft, setDraft] = useState<null | PlausibilityMode>(null);

	const current =
		settings.data?.plausibilityMode ?? ("off" as PlausibilityMode);
	const value = draft ?? current;

	return (
		<div className="max-w-xl">
			<h2 className="text-primary text-base font-semibold">
				Verification mode{selected ? ` — ${selected.name}` : ""}
			</h2>
			<StatusMessage
				error={settings.error}
				isLoading={settings.isLoading}
				loading="Loading settings…"
			/>
			{settings.data && (
				<form
					className="mt-3 flex flex-col gap-3"
					onSubmit={(event) => {
						event.preventDefault();
						update.mutate(value, { onSuccess: () => setDraft(null) });
					}}
				>
					{MODES.map((mode) => (
						<label className="flex items-start gap-2 text-sm" key={mode.value}>
							<input
								checked={value === mode.value}
								className="mt-1"
								name="plausibilityMode"
								onChange={() => setDraft(mode.value)}
								type="radio"
								value={mode.value}
							/>
							<span>
								<span className="text-primary font-medium">{mode.value}</span>
								<span className="text-secondary block text-xs">
									{mode.description}
								</span>
							</span>
						</label>
					))}
					<div className="flex items-center gap-3">
						<Button
							disabled={update.isPending || value === current}
							type="submit"
						>
							{update.isPending ? "Saving…" : "Save"}
						</Button>
						{update.error && (
							<span className="text-error-primary text-sm" role="alert">
								{update.error.message}
							</span>
						)}
						{update.isSuccess && !draft && (
							<span className="text-secondary text-sm">Saved.</span>
						)}
					</div>
				</form>
			)}
			{settings.data && current !== ("off" as PlausibilityMode) && (
				<div className="mt-8 flex flex-col gap-4">
					<BackfillPanel />
					<ReverifyPanel />
				</div>
			)}
		</div>
	);
};
